import React from 'react';
import { Mail, Sparkles, Gauge, Loader2, AlertCircle, ChevronDown } from 'lucide-react';
import './JaugesQuotas.css';

/**
 * Les trois quotas de tête, côte à côte.
 *
 * L'onglet empilait trois blocs complets — Resend, Claude, Google Ads — soit plusieurs écrans
 * de défilement avant de savoir si quelque chose allait mal. Les trois jauges répondent à cette
 * seule question ; le détail s'ouvre au clic, et un seul à la fois.
 *
 * ⚠️ **Claude n'a pas de jauge, et ce n'est pas un oubli.** Anthropic facture à l'usage : il n'y
 * a aucun plafond à lire, donc rien à remplir. Un anneau rempli « au pif » inventerait un
 * maximum que personne ne nous a donné — et un anneau vide se lirait comme 0 %. Sa carte garde
 * donc le même cadre et la même place, mais montre la **tendance** des jours relevés, qui est la
 * seule lecture immédiate honnête pour une facturation sans borne.
 */

/* L'arc : 270°, le creux en bas. Les deux extrémités sont calculées une fois pour toutes —
   135° (bas-gauche) et 45° (bas-droite) sur un cercle de rayon 48 centré en (60, 60). */
const ARC = 'M 26.06 93.94 A 48 48 0 1 1 93.94 93.94';
const LONGUEUR_ARC = 2 * Math.PI * 48 * 0.75;

const nombre = (valeur) => (valeur || 0).toLocaleString('fr-FR');

/** Le seuil de couleur, partagé par les deux jauges qui ont un plafond. */
function etatDuRemplissage(pourcentage) {
  if (pourcentage >= 90) return 'critical';
  if (pourcentage >= 75) return 'warning';
  return 'ok';
}

/**
 * L'anneau. `pourcentage` à `null` ne trace aucun remplissage : c'est le cas d'un quota
 * illimité, où il n'y a rien à remplir — pas zéro.
 */
function Anneau({ pourcentage, etat, children }) {
  const rempli = pourcentage !== null && pourcentage !== undefined;
  return (
    <div className={`jauge-anneau etat-${etat}`}>
      <svg viewBox="0 0 120 120" aria-hidden="true">
        <path className="anneau-fond" d={ARC} />
        {rempli && (
          <path
            className="anneau-remplissage"
            d={ARC}
            strokeDasharray={LONGUEUR_ARC}
            strokeDashoffset={LONGUEUR_ARC * (1 - Math.min(Math.max(pourcentage, 0), 100) / 100)}
          />
        )}
      </svg>
      <div className="anneau-centre">{children}</div>
    </div>
  );
}

/**
 * La tendance de Claude, à la place de l'anneau.
 *
 * L'échelle se prend sur le jour le plus chargé de la fenêtre — il n'y a pas de plafond à
 * rapporter — et la dernière barre est mise en avant : c'est la journée en cours.
 */
function Tendance({ historique }) {
  /*
    ⚠️ **Toute la fenêtre, jamais une tranche.** Les barres étaient coupées aux quatorze
    derniers jours sous une légende qui annonce « sur 30 jours » : un graphique sans axe ne se
    compte pas, il se croit sur parole, et celui-là disait la moitié de ce que le chiffre
    au-dessus affirmait. Le créneau est large en conséquence.
  */
  const jours = historique || [];
  /* Le cadre est rendu même sans jour relevé : il tient la hauteur des trois cartes, et sans
     lui la colonne de Claude remonterait de cent pixels le jour où le relevé manque. */
  const sommet = Math.max(...jours.map((j) => j.jetons.total), 1);
  return (
    <div className="jauge-tendance" aria-hidden="true">
      {jours.map((jour, index) => (
        <span
          key={jour.date}
          className={index === jours.length - 1 ? 'barre derniere' : 'barre'}
          style={{ height: `${Math.max((jour.jetons.total / sommet) * 100, 2)}%` }}
        />
      ))}
    </div>
  );
}

/** Une carte. Un bouton, et non un `div` cliquable : le clavier doit l'atteindre. */
function Jauge({ cle, titre, icone, etat, ouverte, onOuvrir, visuel, valeur, unite, legende }) {
  return (
    <button
      type="button"
      className={`jauge-carte etat-${etat}${ouverte ? ' ouverte' : ''}`}
      onClick={() => onOuvrir(cle)}
      aria-expanded={ouverte}
    >
      <span className="jauge-titre">
        {icone}
        {titre}
      </span>

      {visuel}

      <span className="jauge-valeur">
        {valeur}
        {unite && <span className="jauge-unite"> {unite}</span>}
      </span>

      <span className="jauge-legende">{legende}</span>

      <span className="jauge-ouvrir">
        {ouverte ? 'Masquer le détail' : 'Voir le détail'}
        <ChevronDown size={14} />
      </span>
    </button>
  );
}

export default function JaugesQuotas({
  resendUsage,
  resendLoading,
  claudeUsage,
  claudeLoading,
  quotaStatus,
  quotaLoading,
  selection,
  onSelectionner
}) {
  /* ---------------------------------------------------------------- Resend */
  const jaugeResend = () => {
    const commun = { cle: 'resend', titre: 'Resend', icone: <Mail size={16} /> };

    if (resendLoading && !resendUsage) {
      return { ...commun, etat: 'chargement', visuel: <Anneau pourcentage={null} etat="chargement"><Loader2 size={22} className="spin" /></Anneau>, valeur: '', legende: 'Lecture de la consommation…' };
    }
    if (!resendUsage) {
      return { ...commun, etat: 'absent', visuel: <Anneau pourcentage={null} etat="absent"><Mail size={22} /></Anneau>, valeur: '—', legende: 'Non relevé' };
    }
    if (resendUsage.success === false) {
      return { ...commun, etat: 'erreur', visuel: <Anneau pourcentage={null} etat="erreur"><AlertCircle size={22} /></Anneau>, valeur: '—', legende: 'Relevé indisponible' };
    }
    if (!resendUsage.configure) {
      return { ...commun, etat: 'absent', visuel: <Anneau pourcentage={null} etat="absent"><Mail size={22} /></Anneau>, valeur: '—', legende: 'Aucune clé sur ce serveur' };
    }

    const mois = resendUsage.mois;
    if (mois?.illimite) {
      return { ...commun, etat: 'ok', visuel: <Anneau pourcentage={null} etat="ok"><span className="centre-symbole">∞</span></Anneau>, valeur: nombre(mois.envoyes), unite: 'emails', legende: 'Envois illimités · période de facturation' };
    }
    if (!mois) {
      /* Pas de compteur mensuel rendu : ne rien affirmer, et surtout pas 0 %. */
      return { ...commun, etat: 'absent', visuel: <Anneau pourcentage={null} etat="absent"><Mail size={22} /></Anneau>, valeur: nombre(resendUsage.jour?.envoyes), unite: 'aujourd\'hui', legende: 'Aucun compteur de période rendu' };
    }

    const pourcentage = mois.pourcentage ?? 0;
    const etat = etatDuRemplissage(pourcentage);
    return {
      ...commun,
      etat,
      visuel: <Anneau pourcentage={pourcentage} etat={etat}><span className="centre-pourcent">{pourcentage}<small> %</small></span></Anneau>,
      valeur: `${nombre(mois.utilises)} / ${nombre(mois.plafond)}`,
      unite: 'emails',
      legende: `${nombre(mois.plafond - mois.utilises)} restants sur la période de facturation`
    };
  };

  /* ---------------------------------------------------------------- Claude */
  const jaugeClaude = () => {
    const commun = { cle: 'claude', titre: 'Claude', icone: <Sparkles size={16} /> };

    if (claudeLoading && !claudeUsage) {
      return { ...commun, etat: 'chargement', visuel: <Anneau pourcentage={null} etat="chargement"><Loader2 size={22} className="spin" /></Anneau>, valeur: '', legende: 'Lecture de la consommation…' };
    }
    if (!claudeUsage) {
      return { ...commun, etat: 'absent', visuel: <Anneau pourcentage={null} etat="absent"><Sparkles size={22} /></Anneau>, valeur: '—', legende: 'Non relevé' };
    }
    if (claudeUsage.success === false) {
      return { ...commun, etat: 'erreur', visuel: <Anneau pourcentage={null} etat="erreur"><AlertCircle size={22} /></Anneau>, valeur: '—', legende: 'Relevé indisponible' };
    }

    const { anthropic, interne } = claudeUsage;
    const releve = anthropic?.configure && anthropic?.success !== false ? anthropic : null;

    if (releve) {
      const depense = releve.depense.introuvable
        ? '—'
        : releve.depense.montant.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return {
        ...commun,
        /* Pas de gravité : sans plafond, aucun seuil ne peut être franchi. */
        etat: 'usage',
        visuel: <Tendance historique={releve.historique} />,
        valeur: depense,
        unite: releve.depense.introuvable ? '' : releve.depense.devise,
        legende: `${nombre(releve.jetons.total)} jetons sur ${releve.jours} jours · facturation à l'usage, sans plafond`
      };
    }

    /*
      Le relevé d'Anthropic manque — clé absente, insuffisante, ou organisation sans Admin API.
      La ventilation interne, elle, ne dépend que de notre base : l'afficher plutôt qu'un tiret,
      en **nommant** ce qu'elle est, sinon on la lirait comme le total facturé.
    */
    if (interne && interne.success !== false) {
      return {
        ...commun,
        etat: 'absent',
        visuel: <Tendance historique={[]} />,
        valeur: nombre(interne.jetons),
        unite: 'jetons',
        legende: `Attribués par Kokpit sur ${interne.jours} jours — relevé facturé d'Anthropic non lisible`
      };
    }

    return { ...commun, etat: 'absent', visuel: <Anneau pourcentage={null} etat="absent"><Sparkles size={22} /></Anneau>, valeur: '—', legende: 'Relevé non lisible' };
  };

  /* ------------------------------------------------------------ Google Ads */
  const jaugeGoogle = () => {
    const commun = { cle: 'google', titre: 'Google Ads API', icone: <Gauge size={16} /> };
    const stats = quotaStatus?.quotaStats;

    if (quotaLoading && !quotaStatus) {
      return { ...commun, etat: 'chargement', visuel: <Anneau pourcentage={null} etat="chargement"><Loader2 size={22} className="spin" /></Anneau>, valeur: '', legende: 'Lecture de la consommation…' };
    }
    if (!stats) {
      return { ...commun, etat: 'absent', visuel: <Anneau pourcentage={null} etat="absent"><Gauge size={22} /></Anneau>, valeur: '—', legende: 'Statistiques non relevées' };
    }
    if (stats.isUnlimited) {
      return {
        ...commun,
        etat: 'ok',
        visuel: <Anneau pourcentage={null} etat="ok"><span className="centre-symbole">∞</span></Anneau>,
        valeur: nombre(stats.today.operations),
        unite: 'opérations',
        legende: `Quota illimité · ${stats.accessLevel} · 24 h glissantes`
      };
    }

    const pourcentage = stats.today.percentUsed ?? 0;
    const etat = etatDuRemplissage(pourcentage);
    return {
      ...commun,
      etat,
      visuel: <Anneau pourcentage={pourcentage} etat={etat}><span className="centre-pourcent">{pourcentage}<small> %</small></span></Anneau>,
      valeur: `${nombre(stats.today.operations)} / ${nombre(stats.dailyQuota)}`,
      unite: 'opérations',
      /* « 24 h glissantes », et non « aujourd'hui » : c'est ainsi que Google compte, et c'est
         ce qui rend ce chiffre incomparable à l'historique par jour calendaire du détail. */
      legende: `${stats.accessLevel} · 24 h glissantes · ${stats.today.errors} erreur${stats.today.errors > 1 ? 's' : ''}`
    };
  };

  const jauges = [jaugeResend(), jaugeClaude(), jaugeGoogle()];

  return (
    <div className="jauges-quotas">
      {jauges.map((jauge) => (
        <Jauge
          key={jauge.cle}
          {...jauge}
          ouverte={selection === jauge.cle}
          onOuvrir={onSelectionner}
        />
      ))}
    </div>
  );
}
